import React, { useContext, useEffect, useState } from 'react';
import { AppContext } from '@prisjakt/ui-app-context';
import { FontEmphasis } from '@prisjakt/ui/text';
import { IconDelivery } from '@prisjakt/ui/icon';
import { ShippingCollection, StockStatus } from 'src/types';
import { Tooltip, TooltipPlacement } from 'src/components/tooltip-new';
import {
  ShippingType,
  isStockStatusSupported,
  getDeliveryDaysText,
  getDeliveryDaysTextNew,
  getStandardizedDeliveryText,
  getStandardizedDeliveryTextNew,
} from './utils';
import { Wrapper, IconWrapper, CaptionText } from './styles';
import { MediumUp, MobileOnly } from 'src/ui/block';
import {
  useNewStandardDeliveryTime,
  NewStandardDeliveryTimeType,
} from 'src/hooks/experiments';
import { usePriceRowContext } from '../../../price-row-context';

interface IDeliveryTimeProps {
  shipping?: ShippingCollection | null;
  stockStatus?: StockStatus | null;
}

/** TODO: create translation strings and remove this code after experiment */
const getExperimentPreLabel = (
  experimentVariant: NewStandardDeliveryTimeType,
  stockStatus?: StockStatus | null
) => {
  if (stockStatus !== 'in_stock' && stockStatus !== 'incoming') {
    return '';
  }
  switch (experimentVariant) {
    case 'shipping-label':
      return 'Frakt: ';
    case 'delivery-label':
      return 'Leverans: ';
    default:
      return '';
  }
};

export const DeliveryTime: React.FC<React.PropsWithChildren<
  React.PropsWithChildren<IDeliveryTimeProps>
>> = props => {
  const { shipping, stockStatus } = props;
  const { t } = useContext(AppContext);
  const { forceMobileView } = usePriceRowContext();
  const deliveryDaysText = getDeliveryDaysText(
    t,
    ShippingType.CHEAPEST,
    shipping
  );
  const standardizedDeliveryText = getStandardizedDeliveryText(t, stockStatus);
  const tooltipLabel = t('product', 'delivery-time.label');
  const deliveryTimeText =
    isStockStatusSupported(stockStatus) && deliveryDaysText
      ? deliveryDaysText
      : standardizedDeliveryText;

  // Experiment for new labels for delivery time
  const [experimentVariant, setExperimentVariant] = useState<
    NewStandardDeliveryTimeType
  >(false);
  const experiment = useNewStandardDeliveryTime();
  useEffect(() => {
    setExperimentVariant(experiment);
  }, [experiment]);

  const deliveryDaysTextNew = getDeliveryDaysTextNew(
    t,
    ShippingType.CHEAPEST,
    shipping
  );
  const standardizedDeliveryTextNew = getStandardizedDeliveryTextNew(
    t,
    stockStatus
  );
  const deliveryTimeTextNew =
    getExperimentPreLabel(experimentVariant, stockStatus) +
    (isStockStatusSupported(stockStatus) && deliveryDaysTextNew
      ? deliveryDaysTextNew
      : standardizedDeliveryTextNew);

  if (!deliveryDaysText && !standardizedDeliveryText) {
    return null;
  }

  return (
    <Wrapper>
      {(!experimentVariant || experimentVariant === 'without-label') && (
        <MediumUp $hide={forceMobileView}>
          <Tooltip
            placement={TooltipPlacement.TOP}
            title={`${tooltipLabel}: ${deliveryTimeText}`}
            renderOnMouseOver
          >
            <IconWrapper>
              <IconDelivery color="base400" size={20} />
            </IconWrapper>
          </Tooltip>
        </MediumUp>
      )}

      {experimentVariant ? (
        <CaptionText emphasis={FontEmphasis.MEDIUM}>
          {deliveryTimeTextNew}
        </CaptionText>
      ) : (
        <CaptionText emphasis={FontEmphasis.MEDIUM}>
          <MobileOnly>
            {isStockStatusSupported(stockStatus) && deliveryDaysText}
          </MobileOnly>
          <MediumUp $hide={forceMobileView}>{deliveryTimeText}</MediumUp>
        </CaptionText>
      )}
    </Wrapper>
  );
};
